/**
 * Multi-Organ Scenario Builder
 *
 * Generates coherent multi-organ test datasets for E2E and component tests.
 * Each scenario produces cases, trips, and aircraft that reference the same donor
 * using multi-organ ExternalReference notations (slash, plus, single).
 *
 * @example
 * const scenario = generateMultiOrganScenario({ notation: 'slash', organs: [ENOPOrganType.Heart, ENOPOrganType.Liver] });
 * // scenario.cases   → 2 ActiveCase objects (Heart + Liver) for the same donor
 * // scenario.trip     → MaoTrip with ExternalReference "ABCD123HT/LI"
 * // scenario.aircraft → AircraftStatus with OnFly type carrying the trip
 */
import { faker } from '@faker-js/faker';

import {
  ACTIVE_CASE_STATUSES,
  type ActiveCase,
  ENOPDonorType,
  type MaoTrip,
} from '#entities';
import { ECaseCardStatus, EMaoLegType, ENOPOrganType } from '#shared';

import { generateAirport, generateLocation } from './locationFixtures';

/** Short code mapping for organs */
const ORGAN_SHORT: Record<string, string> = {
  [ENOPOrganType.Heart]: 'HT',
  [ENOPOrganType.Liver]: 'LI',
  [ENOPOrganType.Lungs]: 'LU',
};

export type MultiOrganNotation = 'slash' | 'plus' | 'single';

export interface MultiOrganScenario {
  /** The UNOS donor ID shared across all cases */
  unosDonorId: string;
  /** One ActiveCase per organ */
  cases: ActiveCase[];
  /** The trip with the multi-organ ExternalReference */
  trip: MaoTrip;
  /** Aircraft status carrying the trip (OnFly type) */
  aircraft: AircraftStatusOnFly;
}

/**
 * Minimal AircraftStatus shape for OnFly — keeps the fixture independent
 * of the full AircraftBaseInfo union while matching the API response.
 */
interface AircraftStatusOnFly {
  Id: number;
  Registration: string;
  Status: string;
  Type: 'OnFly';
  Events: unknown[];
  CrewDuties: unknown[];
  Trips: MaoTrip[];
  Trip: MaoTrip;
  Maintenances: unknown[];
  Location: ReturnType<typeof generateLocation>;
  isOnFlight: boolean;
  ActiveTrail: unknown[];
  metaHashes: { locationHash: string; dataHash: string; trailHash: string };
  IsOnCase: boolean;
  IsOnFly: boolean;
  IsReturnToBase: boolean;
  TripLegStart: string | null;
  TripLegStartUnix: number | null;
  TripLegEnd: string | null;
  TripLegEndUnix: number | null;
  Direction: string;
  Airports: ReturnType<typeof generateAirport>[];
}

export interface MultiOrganScenarioOptions {
  /** How organs appear in the ExternalReference */
  notation: MultiOrganNotation;
  /** Organ types for this donor (1–3) */
  organs: ENOPOrganType[];
  /** Override UNOS donor ID (default: random) */
  unosDonorId?: string;
  /** Override donor type (default: random DBD/DCD) */
  donorType?: ENOPDonorType;
  /** Override case status — all cases share the same status */
  status?: (typeof ACTIVE_CASE_STATUSES)[number];
}

/**
 * Build an ExternalReference string based on the notation style.
 */
const buildExternalReference = (
  unosDonorId: string,
  organs: ENOPOrganType[],
  notation: MultiOrganNotation,
  donorType: ENOPDonorType,
): string => {
  const shorts = organs.map(o => ORGAN_SHORT[o] ?? o.slice(0, 2).toUpperCase());

  switch (notation) {
    case 'slash':
      // e.g. "ABCD123HT/LI / DBD HT / Smith"
      return `${unosDonorId}${shorts.join('/')} / ${donorType} ${shorts[0]} / ${faker.person.lastName()}`;

    case 'plus':
      // e.g. "ABCD123LI + ABCD123HT / DBD LI / Smith"
      return organs
        .map(
          (o, i) =>
            `${unosDonorId}${shorts[i]}${i === 0 ? ` / ${donorType} ${shorts[0]} / ${faker.person.lastName()}` : ''}`,
        )
        .join(' + ');

    case 'single':
      // e.g. "ABCD123HT / DBD HT / Smith"
      return `${unosDonorId}${shorts[0]} / ${donorType} ${shorts[0]} / ${faker.person.lastName()}`;
  }
};

const generateCaseForOrgan = (
  unosDonorId: string,
  organType: ENOPOrganType,
  donorType: ENOPDonorType,
  status: (typeof ACTIVE_CASE_STATUSES)[number],
  trip: MaoTrip,
): ActiveCase => {
  const organAbbrev =
    ORGAN_SHORT[organType] ?? organType.slice(0, 2).toUpperCase();
  const transplantCenterCode = faker.string.alpha({
    length: 4,
    casing: 'upper',
  });
  const progress = faker.number.int({ min: 20, max: 80 });

  return {
    id: faker.string.uuid(),
    unosDonorId,
    flightIds: [`N${faker.string.numeric(3)}TX`],
    organ: {
      id: `${faker.string.uuid()}-${organType}`,
      name: organType,
      startPoint: faker.number.int({ min: 80, max: 100 }),
    },
    label: `${unosDonorId}-${organAbbrev}`,
    donorType,
    showOnMap: true,
    transplantCenterCode,
    etaString: `${faker.date.future().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York',
    })} EDT`,
    etaUtc: new Date().toISOString(),
    donorORTimeString: `${faker.date.recent().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York',
    })} EDT`,
    caseTimerEndString: `+${faker.string.numeric(2)}:${faker.string.numeric(2)}`,
    showOrgan: true,
    showReturnToBaseState: false,
    showDonorOr: true,
    showTimer: true,
    showOrganOnCard: true,
    showTransportOnCard: true,
    donorORDateTimeUtc: faker.date.soon().toISOString(),
    status,
    cardStatus: ECaseCardStatus.Ok,
    points: Array.from({ length: 4 }, (_, index) => ({
      id: faker.string.uuid(),
      point: Math.round((index / 3) * 100),
      type: faker.helpers.arrayElement(['airMao', 'donorOR', 'organ']),
      hidden: false,
      subId: `N${faker.string.numeric(3)}TX`,
    })),
    transport: 'airMao',
    progress,
    mapProgress: progress,
    pathLabels: Array.from({ length: 3 }, (_, index) => ({
      id: faker.string.uuid(),
      name: faker.location.city(),
      startPoint: Math.round((index / 2) * 100),
      isActive: index === 1,
    })),
    donorHospital: {
      location: faker.location.streetAddress(),
      address: `${faker.location.city()}, ${faker.location.state({ abbreviated: true })}`,
      state: faker.location.state({ abbreviated: true }),
      lonLat: [
        faker.location.longitude({ min: -100, max: -80 }),
        faker.location.latitude({ min: 30, max: 45 }),
      ],
      timezone: 'America/New_York',
      code: faker.string.alpha({ length: 8, casing: 'upper' }),
    },
    recipientHospital: {
      location: faker.location.streetAddress(),
      address: `${faker.location.city()}, ${faker.location.state({ abbreviated: true })}`,
      state: faker.location.state({ abbreviated: true }),
      lonLat: [
        faker.location.longitude({ min: -100, max: -80 }),
        faker.location.latitude({ min: 30, max: 45 }),
      ],
      timezone: 'America/New_York',
      code: transplantCenterCode,
    },
    trialProgram: null,
    logistics: null,
    serviceRegionId: undefined,
    trips: [trip],
  };
};

/**
 * Generates a coherent multi-organ scenario with linked cases, trip, and aircraft.
 */
export const generateMultiOrganScenario = (
  options: MultiOrganScenarioOptions,
): MultiOrganScenario => {
  const {
    notation,
    organs,
    unosDonorId = faker.string.alpha({ length: 4, casing: 'upper' }) +
      faker.string.numeric(3),
    donorType = faker.helpers.arrayElement([
      ENOPDonorType.DBD,
      ENOPDonorType.DCD,
    ]),
    status = faker.helpers.arrayElement(ACTIVE_CASE_STATUSES),
  } = options;

  const aircraftId = faker.number.int({ min: 1000, max: 9999 });
  const registration = `N${faker.string.numeric(3)}TX`;
  const location = generateLocation(aircraftId);
  const extRef = buildExternalReference(
    unosDonorId,
    organs,
    notation,
    donorType,
  );

  // Build trip
  const now = Date.now();
  const tripStart = now - faker.number.int({ min: 30, max: 120 }) * 60_000;
  const tripEnd = now + faker.number.int({ min: 60, max: 240 }) * 60_000;

  const departureAirport = generateAirport();
  const arrivalAirport = generateAirport();

  const trip: MaoTrip = {
    ExternalReference: extRef,
    Id: faker.number.int({ min: 10000, max: 99999 }),
    TripStartUnix: tripStart,
    TripEndUnix: tripEnd,
    Legs: [
      {
        Id: faker.number.int({ min: 10000, max: 99999 }),
        TripId: 0, // will be patched below
        SectorIndex: 0,
        LegType: EMaoLegType.Charter,
        OffBlocksUtc: new Date(tripStart).toISOString(),
        OffBlocksUtcUnix: tripStart,
        OnBlocksUtc: new Date(tripEnd).toISOString(),
        OnBlocksUtcUnix: tripEnd,
        DepartureAirport: departureAirport,
        ArrivalAirport: arrivalAirport,
        Movement: {
          IsOffBlocks: true,
          IsOnBlocks: false,
          ScheduledOffBlocksUtc: new Date(tripStart).toISOString(),
          ScheduledOffBlocksUtcUnix: tripStart,
          ScheduledOnBlocksUtc: new Date(tripEnd).toISOString(),
          ScheduledOnBlocksUtcUnix: tripEnd,
          Aircraft: { Id: aircraftId, Registration: registration },
        },
      },
    ],
  };
  trip.Legs[0].TripId = trip.Id;

  // Build cases
  const cases = organs.map(organ =>
    generateCaseForOrgan(unosDonorId, organ, donorType, status, trip),
  );

  // Build aircraft status (OnFly)
  const aircraft: AircraftStatusOnFly = {
    Id: aircraftId,
    Registration: registration,
    Status: 'OnFly',
    Type: 'OnFly',
    Events: [],
    CrewDuties: [],
    Trips: [trip],
    Trip: trip,
    Maintenances: [],
    Location: location,
    isOnFlight: true,
    ActiveTrail: [],
    metaHashes: { locationHash: '', dataHash: '', trailHash: '' },
    IsOnCase: true,
    IsOnFly: true,
    IsReturnToBase: false,
    TripLegStart: new Date(tripStart).toISOString(),
    TripLegStartUnix: tripStart,
    TripLegEnd: new Date(tripEnd).toISOString(),
    TripLegEndUnix: tripEnd,
    Direction: `${departureAirport.IATA} → ${arrivalAirport.IATA}`,
    Airports: [departureAirport, arrivalAirport],
  };

  return { unosDonorId, cases, trip, aircraft };
};

/**
 * Pre-built named scenarios for E2E mocks.
 */
export const MULTI_ORGAN_SCENARIOS = {
  /**
   * Heart + Liver via slash notation: "ABCD123HT/LI"
   */
  slashHeartLiver: () =>
    generateMultiOrganScenario({
      notation: 'slash',
      organs: [ENOPOrganType.Heart, ENOPOrganType.Liver],
      unosDonorId: 'DEMO001',
      donorType: ENOPDonorType.DBD,
    }),

  /**
   * Liver + Lungs via plus separator: "ABCD123LI + ABCD123LU"
   */
  plusLiverLungs: () =>
    generateMultiOrganScenario({
      notation: 'plus',
      organs: [ENOPOrganType.Liver, ENOPOrganType.Lungs],
      unosDonorId: 'DEMO002',
      donorType: ENOPDonorType.DCD,
    }),

  /**
   * Single organ (Heart) — regression baseline
   */
  singleHeart: () =>
    generateMultiOrganScenario({
      notation: 'single',
      organs: [ENOPOrganType.Heart],
      unosDonorId: 'DEMO003',
      donorType: ENOPDonorType.DBD,
    }),
} as const;
